import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK, doSetupSsoUpdate } from 'store/actions';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual } from 'lodash';
import { useSelector } from 'hooks/useSelector';
import {
  selectRemoteOrganisationId,
  selectSsoSettings,
} from 'store/remote/selectors';
import { TextField } from 'components/Field/TextField';
import { smartText } from 'lib/str';
import { SsoOrganisation } from 'types/SsoSettings';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';

export const SsoSettingsIdpModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsSsoIdp>
> = ({ navigation }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const organisationId = useSelector(selectRemoteOrganisationId)!;
  const ssoSettings = useSelector(selectSsoSettings)!;
  const dataRef = React.useRef<SsoOrganisation>(cloneDeep(ssoSettings));

  const isDirty = React.useCallback(
    () => !isEqual(ssoSettings, dataRef.current),
    [ssoSettings],
  );
  const isDirtyRef = React.useRef<boolean>(false);
  isDirtyRef.current = isDirty();

  const handleSave = React.useCallback(() => {
    dispatch(
      doSetupSsoUpdate({
        organisationId,
        samlIdpEntityId: dataRef.current.samlIdpEntityId,
        samlIdpCertificate: dataRef.current.samlIdpCertificate,
      }),
    );
  }, [dispatch, organisationId]);

  const handleClose = React.useCallback(() => {
    if (isDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () =>
          dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsSsoIdp])),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsSsoIdp]));
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  const handleChangeValue = <K extends keyof typeof dataRef.current>(
    key: K,
    value: (typeof dataRef.current)[K],
    doForceUpdate: boolean = false,
  ) => {
    dataRef.current[key] = value;
    if (doForceUpdate || isDirtyRef.current !== isDirty()) {
      forceUpdate();
    }
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty() });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="SAML IdP"
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card
        explanation={smartText(
          'Be careful when changing these settings. ' +
            "Misconfiguring them can cause your user's SSO requests to fail",
        )}
      />
      <Card label="IdP Entity ID">
        <TextField
          placeholder="No IdP Entity ID"
          onChange={(value) => handleChangeValue('samlIdpEntityId', value)}
          value={dataRef.current.samlIdpEntityId}
          onEsc={handleClose}
          autoFocusWeb
          autoFocusDelay
        />
      </Card>
      <Card label="IdP Certificate">
        <TextAreaField
          placeholder="No IdP Certificate"
          onChange={(value) => handleChangeValue('samlIdpCertificate', value)}
          value={dataRef.current.samlIdpCertificate}
          onEsc={handleClose}
          showFieldTypeIcon={false}
        />
      </Card>
    </PageScrollView>
  );
};
